// Status transition buttons for issue detail
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ArrowRight, CheckCircle, PlayCircle, Clock, XCircle, Loader2 } from 'lucide-react'

interface IssueStatusActionsProps {
  issueId: string
  currentStatus: string
}

const TRANSITIONS: Record<string, { next: string; label: string; icon: React.ReactNode; color: string }[]> = {
  draft: [
    { next: 'open', label: 'Submit Issue', icon: <ArrowRight className="mr-1.5 h-4 w-4" />, color: 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' },
  ],
  open: [
    { next: 'in_progress', label: 'Start Working', icon: <PlayCircle className="mr-1.5 h-4 w-4" />, color: 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' },
  ],
  in_progress: [
    { next: 'waiting_info', label: 'Waiting Info', icon: <Clock className="mr-1.5 h-4 w-4" />, color: 'bg-amber-600 text-white hover:bg-amber-700' },
    { next: 'resolved', label: 'Mark Resolved', icon: <CheckCircle className="mr-1.5 h-4 w-4" />, color: 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' },
  ],
  waiting_info: [
    { next: 'in_progress', label: 'Resume', icon: <PlayCircle className="mr-1.5 h-4 w-4" />, color: 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' },
  ],
  resolved: [
    { next: 'closed', label: 'Close Issue', icon: <XCircle className="mr-1.5 h-4 w-4" />, color: 'bg-kaycoo-charcoal text-white hover:bg-kaycoo-charcoal-2' },
    { next: 'in_progress', label: 'Reopen', icon: <PlayCircle className="mr-1.5 h-4 w-4" />, color: 'border-kaycoo-teal-dark text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale' },
  ],
  closed: [
    { next: 'open', label: 'Reopen Issue', icon: <PlayCircle className="mr-1.5 h-4 w-4" />, color: 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' },
  ],
}

export function IssueStatusActions({ issueId, currentStatus }: IssueStatusActionsProps): JSX.Element {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [transitioning, setTransitioning] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async (newStatus: string) => {
      setTransitioning(newStatus)
      const updateData: Record<string, unknown> = { status: newStatus }
      if (newStatus === 'resolved') {
        updateData.resolved_at = new Date().toISOString()
        updateData.resolved_by = user?.id
      }
      if (newStatus === 'open' || newStatus === 'in_progress') {
        // Reopening — clear resolution data
        updateData.resolved_at = null
        updateData.resolved_by = null
      }
      const { error } = await supabase
        .from('issues')
        .update(updateData as never)
        .eq('id', issueId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issue-detail', issueId] })
      queryClient.invalidateQueries({ queryKey: ['issues'] })
    },
    onSettled: () => setTransitioning(null),
  })

  const transitions = TRANSITIONS[currentStatus] ?? []

  if (transitions.length === 0) {
    return (
      <Card className="border-kaycoo-teal-light bg-kaycoo-white">
        <CardContent className="py-4 text-center text-sm text-kaycoo-charcoal-2">
          This issue is closed.
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-kaycoo-teal-light bg-kaycoo-white">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm text-kaycoo-teal-dark">
          <ArrowRight className="h-4 w-4" />
          Actions
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        {transitions.map((t, idx) => (
          <Button
            key={t.next}
            onClick={() => mutation.mutate(t.next)}
            disabled={mutation.isPending}
            className={t.color}
            variant={idx > 0 && transitions.length > 1 ? 'outline' : 'default'}
            size="sm"
          >
            {transitioning === t.next ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : t.icon}
            {t.label}
          </Button>
        ))}
      </CardContent>
    </Card>
  )
}
